import React, { useCallback, useEffect, useRef } from "react";
import { useState } from "react";
import { useDispatch, useStore } from "react-redux";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import { Spinner } from "react-bootstrap";

import { Link, useNavigate, useSearch } from "../utils/components/router";
import { encode, hasCrypto } from "../utils/functions/crypt";
import { basketActions, configActions, kundenActions } from "../actions";
import { orderFunctions, scrollToTop } from "../utils/functions";
import { RESPONSE_HEADER } from "../utils/enums";
import CONFIG from '../config';

let loading = false;

const TanAuth = (props) => {
  const refForIframeHeight = useRef(null);
  const { postOrderData, loadData } = props || {};
  const params = useSearch();

  const [state, setState] = useState({ warningMsg: "", showErrorMsg: false });
  const [loaded, setLoaded] = useState(false);
  const [tanRequested, setTanRequested] = useState(localStorage.getItem("tanRequested") === "yes");
  const { configStorage, basketStorage, authenticationStorage } = useStore().getState();
  const dispatch = useDispatch();
  const navigate = configStorage.contextNavigate(useNavigate());

  const handleResize = async () => {
    if (window.innerWidth < 500) {
      const iframe = window.parent.document.getElementsByTagName('iframe')[0];
      if (iframe && refForIframeHeight && refForIframeHeight.current) {        
        let iFrameHeight = refForIframeHeight.current.scrollHeight;
        console.log("Component height from ref: " + iFrameHeight);
        
        if (iFrameHeight < 850)  {
          iframe.style.height = '1202px';
        } else {
          iframe.style.height = iFrameHeight + 'px';  
        }
        console.log("Reset iframe height to: " + iframe.style.height);
      } else {
        console.log("Rendering to slow. No size available. Use static height 1000px");
        iframe.style.height = '1201px'; 
      }
    }
  }  
    
  // Load data only on init
  useEffect(() => {
    const auftragsNummer = !isNaN(params.auftragsNummer) ? Number(params.auftragsNummer) : basketStorage.warenkorbId;
    const kundenId = authenticationStorage.kundenId;
    if (loadData === "true" && !loaded && auftragsNummer && kundenId && !loading) {
      loading = true;
      let tanApiCall = configStorage.getApiRequest("getTanAuthDaten");
      tanApiCall({
        ...params,
        tanAuth: params.tanAuth !== undefined ? String(params.tanAuth).match(/^(yes|true)$/) : !params.error,
        auftragsNummer,
      }, (error, data, _responseData) => {
        setLoaded(true);
        dispatch(configActions.restartSession(configStorage.getRequestData("session_ping")));
        dispatch(basketActions.fetchMasterData(configStorage.getRequestData("masterdata")));
        const requestDataKundenEntry = configStorage.getRequestData("kunde");
        requestDataKundenEntry.url = requestDataKundenEntry.url + kundenId;
        // Zuerst die Kundendaten laden, bevor der response von getTanAuthDaten ausgewertet wird
        dispatch(kundenActions.fetchEntry(requestDataKundenEntry))
          .then(() => {
            resetData();            
            if (error) {
              const msg = orderFunctions.processOrderError(error);
              if (msg.error) {
                setState({ showErrorMsg: true, warningMsg: msg.error });
                navigate("/error", msg.error);
              }
            } else {
              const errorMsg = orderFunctions.processResponse({ data }, authenticationStorage.kundenId, false);        
              switch (data?.header?.status) {
                case RESPONSE_HEADER.STATUS.NO_SUCCESS:
                  if (data.header.errorNr === RESPONSE_HEADER.ERRORNUMMER.KONTODECKUNG) {
                    navigate("/error", { messageText: RESPONSE_HEADER.ERRORMESSAGE.KONTODECKUNG_KUNDE });
                  } else if (data.header.errorNr === RESPONSE_HEADER.ERRORNUMMER.BANKAPI_KUNDE_NICHT_BERECHTIGT) {
                    let messageText = RESPONSE_HEADER.ERRORMESSAGE.NICHT_BERECHTIGT_KUNDE;
                    navigate("/error", { messageText: messageText.map(line => line.replace(/\{TIMESTAMP\}/, new Date().toLocaleString()))});
                  } else if (data.header.errorNr === RESPONSE_HEADER.ERRORNUMMER.BANKAPI_FORBIDDEN ||
                    data.header.errorNr === RESPONSE_HEADER.ERRORNUMMER.BANKAPI_INTERNAL_SERVER_ERROR ||
                    data.header.errorNr === RESPONSE_HEADER.ERRORNUMMER.BANKAPI_GENERAL_ERROR) {
                    navigate("/error", { messageText: RESPONSE_HEADER.ERRORMESSAGE.BANK_API_FEHLER });
                  } else if (data.header.errorNr === RESPONSE_HEADER.ERRORNUMMER.TAN_REJECTED) {
                    navigate("/error", { messageText: RESPONSE_HEADER.ERRORMESSAGE.TAN_ERROR_RESPONSE });
                  } else {
                    setState({ showErrorMsg: true, warningMsg: errorMsg.error });
                    navigate("/error", errorMsg.error);
                  }
                  break;
                case RESPONSE_HEADER.STATUS.ERROR:
                  let messageText = RESPONSE_HEADER.ERRORMESSAGE.TAN_ORDER_PROCESS_ERROR_RESPONSE;
                  navigate("/error", { messageText: messageText.map(line => line.replace(/\{ERROR_CODE\}/, data.header.errorNr || "UNKNOWN")) });
                  break;
                default: // AUTHORIZED oder UNKNOWN                  
                  dispatch(basketActions.setBankApiResponseData(data));

                  if (errorMsg.error) {
                    navigate("/error", errorMsg.error);
                  } else if (errorMsg.path && errorMsg.message) {
                    navigate(errorMsg.path, { errorMsg: errorMsg.message });
                  } else if (errorMsg.path) {
                    navigate(errorMsg.path);
                  } else {
                    navigate("/error", { messageText: RESPONSE_HEADER.ERRORMESSAGE.NO_SUCCESS_KUNDEN });
                  } 

                  break;
              }
            }
          })
          .catch(error => {
            resetData();
            const msg = orderFunctions.processOrderError(error);
            if (msg.error) {
              setState({ showErrorMsg: true, warningMsg: msg.error });
              navigate("/error", msg.error);
            }
          });
      });
    };
  }, [basketStorage.warenkorbId, authenticationStorage.kundenId, params, loadData, configStorage, navigate, dispatch, loaded]);

  useEffect(() => {
    handleResize();
    scrollToTop();
    setTimeout(() => {
      handleResize();
      scrollToTop();
    }, 2000);
  }, []);

  const redirectToTan = useCallback(async () => {
    if (!postOrderData) return;
    if (!tanRequested) {
      setTanRequested(true);
      const requestData = configStorage.getRequestData("order", "post");
      const config = {
        headers: {
          ...(requestData.config ? requestData.config.headers || {} : {}),
          request: JSON.stringify(postOrderData.header || {})
        }
      };
      const axiosVerify = axios.create(config);
      // TODO: RageApi addEmTanOrder bzw addSoTanOrder
      const tanResponse = await axiosVerify[requestData.request.type](requestData.url + basketStorage.warenkorbId, postOrderData)
        .catch(error => {
          return orderFunctions.processOrderError(error);
        });
      if (tanResponse.error) {
        resetData();
        setState({ showErrorMsg: true, warningMsg: tanResponse.error });
        return navigate("/error", tanResponse.error);
      }

      let data = tanResponse ? tanResponse.data || null : null;
      const sessionData = { ...postOrderData, warenkorbId: basketStorage.warenkorbId };
      if (hasCrypto()) {
        await encode(JSON.stringify(sessionData), configStorage.getAppId())
          .then(tanSessionStore => {
            data = { ...data, sessionData: { tanSessionStore } };
          })
          .catch(e => {
            console.debug("encryption failed", e);
            data = { ...data, sessionData };
          })
          .finally(() => {
            localStorage.setItem("tanRequested", "yes");
          });
      } else {
        data = { ...data, sessionData };
        localStorage.setItem("tanRequested", "yes");
      }
      if (data.sessionData) {
        Object.keys(data.sessionData).forEach(key => {
          localStorage.setItem(key, JSON.stringify(data.sessionData[key]));
        });
      }
      dispatch(configActions.restartSession(configStorage.getRequestData("session_ping")));
      if (typeof window?.OSPlusNeo?.reportSuccess === "function") {
        window.OSPlusNeo.reportSuccess();
        // call mocking server in dev mode
        if (CONFIG.NODE_ENV === 'development' && data.redirectUrl) {
          window.location.assign(data.redirectUrl);
        }
      }
    }
  }, [basketStorage.warenkorbId, configStorage, dispatch, navigate, postOrderData, tanRequested]);

  const resetData = () => {
    const tanRequested = localStorage.getItem("tanRequested");
    loading = false;
    if (tanRequested !== "yes") return;
    localStorage.removeItem("tanRequested");
    localStorage.removeItem("kundenId");
    localStorage.removeItem("header");
    localStorage.removeItem("tanSessionStore");
  };

  if (loadData) {
    return <div className="bootstrap-iso">
      <Spinner animation="border" role="status" variant="primary">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>;
  } else {
    scrollToTop();
    return (
      <div ref={refForIframeHeight} className="if6_outer section if6_section">
        <div className="bootstrap-iso">
          <div className="container p-one h-100">
            <Modal.Dialog size="lg">
              <Modal.Header>
                <h3 className="col-12 text-secondary">
                  <span className="">
                    Für Ihre Bestellung ist eine sichere Identifikation erforderlich.
                  </span>
                </h3>
              </Modal.Header>
              <Modal.Body>
                <div className="col-12">
                  {state.showErrorMsg ?
                    <div className="ui error message text-danger">
                      <div className="header">
                        {state.warningMsg.messageTitle ? state.warningMsg.messageTitle : state.warningMsg}
                      </div>
                      {state.warningMsg.messageText && Array.isArray(state.warningMsg.messageText)
                        ? state.warningMsg.messageText.map(line => <div>{line}</div>)
                        : null
                      }
                    </div>
                    : ""
                  }
                </div>
                <div className="col-12">
                  {!state.showErrorMsg ?
                    <div className="">
                      {tanRequested
                        ? <div className="header">Eine TAN Authentifizierung wird gerade bearbeitet</div>
                        : <div className="header">Hierzu werden sie zur TAN Authentifizierung der Sparkasse weitergeleitet</div>
                      }
                    </div> : ""}
                </div>
                <div className="col-12 justify-content-center mt-one">
                  <button
                    className="btn btn-default col-6 col-lg-4 text-center"
                    onClick={redirectToTan}
                    disabled={tanRequested}
                    id="admin-btn"
                    // data-bs-toggle="modal"
                    data-bs-target="#flipFlopp"
                  >
                    Weiter zu meinem Online-Banking
                  </button>
                </div>
              </Modal.Body>
            </Modal.Dialog>
            <div className="mb-one mt-one">
              <Link
                to={configStorage.getBackToShopLink()}
                className="btn btn-info col-6 col-md-4 col-lg-2"
                onClick={resetData}
              >
                Zurück zum Shop
              </Link>
              <div className="col-lg-7"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
};
export default TanAuth;
